import { fetchUserById } from 'lib/models/users';
import { useEffect, useMemo, useState } from 'react';

import useLoggedUserDocument from 'hooks/useLoggedUserDocument';

import useUserId from './useUserId';

type UseUserResponseType =
	| {
			loading: false;
			user: Omit<User, '_id'> & { _id: string; fullName: string };
			fromParams: boolean;
	  }
	| {
			loading: true;
			user: null;
			fromParams: boolean;
	  };

/**
 * Returns the users associated with the id received in parameter or the logged user
 */
function useUser() {
	const [loading, setLoading] = useState<boolean>(true);
	const [user, setUser] = useState<User | null>();
	const { id: userId, fromParams } = useUserId();
	const loggedUser = useLoggedUserDocument();

	useEffect(() => {
		async function getUser() {
			if (userId && userId !== 'new' && fromParams) {
				const response = await fetchUserById(userId);
				setUser(response);
			} else {
				setUser(loggedUser);
			}
			setLoading(false);
		}

		getUser();
	}, [userId, loggedUser, fromParams]);

	const fullName = `${(user?.last_name ?? '').toUpperCase() ?? ''} ${(user?.first_name ?? '').toUpperCase()}`;

	const data: UseUserResponseType = useMemo(() => {
		const { _id = '' } = user || {};
		return !loading && !!user
			? {
					loading: false,
					user: { ...user, _id, fullName },
					fromParams,
			  }
			: { loading: true, user: null, fromParams };
	}, [user, fromParams, loading, fullName]);

	return data;
}

export default useUser;
